import * as React from "react";
import {useEffect, useState} from "react";
import {IPlanRelativePerformance, PortfolioApi} from "../../api/PortfolioApi";
import {IPlanTreeQuarterlyPerformance} from "../../model/Portfolio.model";
import {formatMonthYearDate, formatShortDate} from "../../utils/dateUtil";
import {ErrorComponent} from "../base/Error.component";
import {HorizontalBarChart, IHorizontalBarChart} from "../common/HorizontalBarChart.component";
import {formatXAxisPeriodToYearForPlanMarketValue} from "../../utils/chartUtils";
import {formatPercentBasedOnLocale} from "../../utils/numberUtil";


export const QUARTER_PLAN_TITLE = "Quarterly Plan Performance";
export const QUARTER_RELATIVE_PLAN_TITLE = "Quarterly Plan Relative Performance";
export const QUARTERLY_EXCESS_PERFORMANCE = "Quarterly Excess Performance";

export const QuarterlyPerformance: React.FunctionComponent = () => {
    const [quarterlyPerformance, setQuarterlyPerformance]
        = useState<IPlanTreeQuarterlyPerformance | undefined>(undefined);
    const [relativePerformance, setRelativePerformance] = useState<IHorizontalBarChart[]>([]);
    const [apiError, setApiError] = useState<boolean>(false);

    const getRelativePerformanceChartData =
        (relativePerformance: IPlanRelativePerformance[]): IHorizontalBarChart[] => {
        return relativePerformance.map((performance) => {
            return {
                label: formatShortDate(performance.date),
                value: performance.relativeReturn
            };
        });
    };

    useEffect(() => {
        PortfolioApi.getCurrentPlanQuarterlyPerformance().then((response) => {
          setQuarterlyPerformance(response);
        }).catch(() => {
            setApiError(true);
        });

        PortfolioApi.getCurrentPlanRelativePerformances().then((response) => {
            setRelativePerformance(getRelativePerformanceChartData(response));
        }).catch(() => {
            setApiError(true);
        });
    }, []);

    const getClassName = (value: number): string => {
        return value >= 0 ? "quarterly-performance__percentage positive" : "quarterly-performance__percentage negative";
    };

    const renderQuarterPlanPerformance = (title: string,
                                          date: Date,
                                          percentageValue: number,
                                          benchmarkName?: string) => {
        return <div className={"quarterly-performance__absolute-relative-container"}>
                    <h3>{title}</h3>
                    <div className={"quarterly-performance__as-of-date-benchmark-name"}>
                        {`as of ${formatMonthYearDate(date)}`}
                    </div>
                    <div className={getClassName(percentageValue)}>{percentageValue}%</div>
                    {benchmarkName && (
                        <div className={"quarterly-performance__as-of-date-benchmark-name"}>
                            {`Compared to ${benchmarkName}`}
                        </div>
                    )}
                </div>;
    };

    const chartValueFormatFunction = (value: number | string) => {
        if (typeof value === "string") {
            return `Excess return: ${value}`;
        }
        return `Excess return: ${value}%`;
    };

    const renderQuarterExcessPerformanceChart = () => {
        return <div className={"quarterly-performance__chart-container"}>
            <h3>{QUARTERLY_EXCESS_PERFORMANCE}</h3>
            <HorizontalBarChart
                data={relativePerformance}
                tooltipTitle={"Excess Return"}
                valueFormatFunction={chartValueFormatFunction}
                chartHeight={275}
                xAxisFormater={formatXAxisPeriodToYearForPlanMarketValue}
                yAxisFormater={value => formatPercentBasedOnLocale(value)}
            />
        </div>;
    };

    const hasQuarterlyPerformance = (quarterAbsoluteOrRelative?: number | null): boolean =>
        quarterAbsoluteOrRelative !== undefined && quarterAbsoluteOrRelative !== null;

    const hasQuarterlyData = (): boolean =>
        hasQuarterlyPerformance(quarterlyPerformance?.quarterAbsolute)
        || hasQuarterlyPerformance(quarterlyPerformance?.quarterRelative)
        || relativePerformance.length  > 0 ;

    const renderComponent = () => {
        if (apiError) {
            return <ErrorComponent/>;
        }

        return (
            hasQuarterlyData() &&
            <div className={"quarterly-performance__container"} data-testid={"quarterly-performance__container"}>
                <div className={"quarterly-performance__performance-container"}>
                    {
                        hasQuarterlyPerformance(quarterlyPerformance?.quarterAbsolute)
                        ? renderQuarterPlanPerformance(
                              QUARTER_PLAN_TITLE,
                              quarterlyPerformance!.date,
                              quarterlyPerformance!.quarterAbsolute!
                          )
                        : null
                    }
                    {
                        hasQuarterlyPerformance(quarterlyPerformance?.quarterRelative)
                        ? renderQuarterPlanPerformance(
                            QUARTER_RELATIVE_PLAN_TITLE,
                            quarterlyPerformance!.date,
                            quarterlyPerformance!.quarterRelative!,
                            quarterlyPerformance!.benchmarkName
                          )
                        : null
                    }
                </div>
                {relativePerformance.length > 0 && renderQuarterExcessPerformanceChart()}
            </div>
        );
    };

    return renderComponent();
};